/* eslint-disable no-console */
import Vue from 'vue';
import { get } from '@/helpers/getUrlParameters';
import type { FacebookPixelFunction } from './facebook-pixel';
import { fbq } from './facebook-pixel';
import type { DataLayerEntry, Item } from './google-analytics';
import { gtag } from './google-analytics';

declare global {
  interface Window {
    dataLayer: DataLayerEntry[];
    fbq: FacebookPixelFunction;
    _fbq: unknown;
    gtag: typeof gtag;
  }
}

/**
 * Set page context for GA4
 * @param pageContext - Page context
 * @param pageContext.page_lang - Page language
 * @param pageContext.org_id - Organization ID
 * @param pageContext.campaign_id - Campaign ID
 * @param pageContext.campaign_title - Campaign title
 * @param pageContext.team_id - Team ID
 * @param pageContext.team_name - Team name
 *
 * @example
 * setPageContext({
 *   page_lang: 'en',
 *   org_id: 123456,
 *   campaign_id: 123456,
 *   campaign_title: 'otezm-idf',
 *   team_id: 123456,
 *   team_name: 'example',
 * })
 */
export function setPageContext(
  pageContext: {
    page_lang?: string;
    org_id?: number;
    campaign_id?: number;
    campaign_title?: string;
    team_id?: number;
    team_name?: string;
  },
) {
  Object.entries(pageContext).filter(([, value]) => Boolean(value)).forEach(([key, value]) => {
    gtag('set', key, value.toString());
  });

  const clientId = get('client_id');
  const sessionId = get('session_id');

  if (clientId) {
    gtag('set', 'client_id', clientId);
  }

  if (sessionId) {
    gtag('set', 'session_id', sessionId);
  }
}

export const waitAnalyticsUpdate = Vue.nextTick;

export function getServerConversionIDLabel(params: {
  tracking_org_google_conversion_id?: string,
  tracking_org_google_conversion_label?: string,
}): {
  conversionId: string,
  conversionLabel?: string,
  sendTo?: string,
} | undefined {
  if (!params.tracking_org_google_conversion_id) {
    return undefined;
  }

  const data: ReturnType<typeof getServerConversionIDLabel> = {
    conversionId: `AW-${params.tracking_org_google_conversion_id}`,
  }

  if (params.tracking_org_google_conversion_label) {
    data.conversionLabel = params.tracking_org_google_conversion_label;
    data.sendTo = `${data.conversionId}/${params.tracking_org_google_conversion_label}`;
  }

  return data;
}

/**
 * @param value - The lowest value available for the campaign or 0
 * @param currency - Three letter currency code
*/
export function eventConversion(value: number, currency: string, sendTo?: string) {
  if (!sendTo) {
    return;
  }

  gtag('event', 'conversion', {
    send_to: sendTo,
    value,
    currency,
  });
}

/**
 * Simplified item type for GA4
 * @param item_id - Campaign ID
 * @param item_category - Organization ID
 * @param item_brand - Organization name
 * @param affiliation - Team ID
 * @param item_list_id - To associate with an item list viewed
 * @param item_list_name - To associate with an item list viewed
 * @param index - The position in the item list (if applicable)
 */
type SimplifiedItem = Pick<
  Item,
  'item_id' | 'item_name' | 'price' | 'item_category' | 'item_brand' | 'affiliation' | 'item_list_id' | 'item_list_name' | 'quantity'
>;

/**
 * When a user views an organization page with multiple campaigns
 * @param orgId - The ID of the organization
 * @param orgName - The name of the organization
 */
export function eventViewOrganizationHP(orgId: number, orgName: string, items: SimplifiedItem[]) {
  gtag('event', 'view_item_list', {
    item_list_id: orgId.toString(),
    item_list_name: orgName,
    items: items.map(i => ({
      ...i, quantity: 1, item_list_id: orgId.toString(), item_list_name: orgName,
    })),
  });
}

/**
 * When a user views a specific campaign page
 * @param campaignId - The ID of the campaign
 * @param value - The lowest value available for the campaign or 0
 * @param currency - Three letter currency code
 */
export function eventViewCampaign(campaignId: number, value: number, currency: string, items: SimplifiedItem[]) {
  gtag('event', 'view_item', {
    value,
    currency,
    items,
  });

  fbq('track', 'ViewContent', {
    value,
    currency,
    contents: [
      {
        id: campaignId.toString(),
        quantity: 1,
      },
    ],
    content_type: 'product',
  });
}

/**
 * When a user opens the donate popup (to send on modal open, not on click)
 * @param value - The total value of the purchase
 * @param currency - Three letter currency code
 */
export function eventBeginCheckout(value: number, currency: string, items: SimplifiedItem[]) {
  gtag('event', 'begin_checkout', {
    value,
    currency,
    items,
  });

  fbq('track', 'InitiateCheckout', {
    value,
    currency,
    contents: items.map(i => ({ id: i.item_id, quantity: 1 })),
    content_type: 'product',
  });
}

/**
 * When a user clicks to open the Credit Card/Bit payment window
 *
 * Note: On desktop, bit link sends an SMS to your mobile device, TYP opens on desktop
 *
 * @param paymentType - gateway name
 * @param value - The total value of the purchase
 * @param currency - Three letter currency code
 */
export function eventAddPaymentInfo(paymentType: string, value: number, currency: string, items: SimplifiedItem[]) {
  gtag('event', 'add_payment_info', {
    payment_type: paymentType,
    value,
    currency,
    items,
  });

  fbq('track', 'AddPaymentInfo', {
    value,
    currency,
    contents: items.map(i => ({ id: i.item_id, quantity: 1 })),
    content_type: 'product',
  });
}

/**
 * When a user completes a successful payment (Thank you page after successful routing)
 *
 * @param paymentType - gateway name
 * @param value - The total value of the purchase
 * @param currency - Three letter currency code
 * @param transactionId - Donation ID
 */
export function eventPurchase(
  paymentType: string,
  value: number,
  currency: string,
  transactionId: string,
  items: SimplifiedItem[],
) {
  gtag('event', 'purchase', {
    payment_type: paymentType,
    value,
    currency,
    transaction_id: transactionId,
    items,
  });

  fbq('track', 'Purchase', {
    value,
    currency,
    contents: items.map(i => ({ id: i.item_id, quantity: 1 })),
    content_type: 'product',
  });
}

/**
 * When a user completes a successful refund
 *
 * @param paymentType - gateway name
 * @param value - The actual value **refunded** to the user
 * @param currency - Three letter currency code
 * @param transactionId - Donation ID, **must match the original purchase**
 */
export function eventRefund(
  paymentType: string,
  value: number,
  currency: string,
  transactionId: string,
  items: SimplifiedItem[],
) {
  gtag('event', 'refund', {
    payment_type: paymentType,
    value,
    currency,
    transaction_id: transactionId,
    items,
  });
}
